import React, { useState, useCallback, useMemo } from 'react'
import { FaCalendarAlt } from 'react-icons/fa'
import moment from 'moment'
import SelectDateSyncData from './SelectDateSyncData'
import { defaultFilters } from '../hooks/useDashboardFilters'
import { DASHBOARD_TIME_RANGE, FILTER_STATUSES_DASHBOARD } from '../../../utils/constant'

function FilterBar({
  filters,
  updateFilter,
  resetFilters,
  events = [],
  onChange,
}) {
  const [dateModalOpen, setDateModalOpen] = useState(false)
  const handleTimeRangeChange = useCallback((range) => {
    let start = null
    let end = null

    if (range === DASHBOARD_TIME_RANGE.DAYS_90) {
      start = moment().subtract(90, 'days').startOf('day').unix()
      end = moment().endOf('day').unix()
    } else if (range === DASHBOARD_TIME_RANGE.THIS_WEEK) {
      start = moment().subtract(7, 'days').startOf('day').unix()
      end = moment().endOf('day').unix()
    } else if (range === DASHBOARD_TIME_RANGE.THIS_MONTH) {
      start = moment().subtract(1, 'month').startOf('day').unix()
      end = moment().endOf('day').unix()
    }

    updateFilter({
      time: range,
      start,
      end,
      eid: null,
      status: null,
    })

    if (onChange) {
      onChange({
        ...filters,
        time: range,
        start,
        end,
        eid: null,
        status: null,
      })
    }
  }, [updateFilter, onChange, filters])

  const handleCustomDateChange = useCallback((rangeDate) => {
    const formatStartDate = new Date(rangeDate[0].startDate.setHours(0, 0, 0, 0))
    const formatEndDate = new Date(rangeDate[0].endDate.setHours(23, 59, 59, 999))

    const start = Math.floor(formatStartDate.getTime() / 1000)
    const end = Math.floor(formatEndDate.getTime() / 1000)

    updateFilter({
      time: DASHBOARD_TIME_RANGE.CUSTOM,
      start,
      end,
      eid: null,
      status: null,
    })

    if (onChange) {
      onChange({
        ...filters,
        time: DASHBOARD_TIME_RANGE.CUSTOM,
        start,
        end,
        eid: null,
        status: null,
      })
    }
    setDateModalOpen(false)
  }, [updateFilter, onChange, filters])

  const handleEventChange = useCallback((e) => {
    const { value } = e.target
    const eid = value === '' ? null : value

    if (eid) {
      const newFilters = {
        ...filters,
        eid,
        time: null,
        start: null,
        end: null,
        range: null
      }
      updateFilter(newFilters)
      if (onChange) {
        onChange(newFilters)
      }
    } else {
      const newFilters = {
        ...filters,
        eid: null,
        time: defaultFilters.time,
        start: defaultFilters.start,
        end: defaultFilters.end,
      }
      updateFilter(newFilters)
      if (onChange) {
        onChange(newFilters)
      }
    }
  }, [updateFilter, onChange, filters])

  const handleStatusChange = useCallback((e) => {
    const { value } = e.target
    updateFilter('status', value)
    if (onChange) {
      onChange({
        ...filters,
        status: value,
      })
    }
  }, [updateFilter, onChange, filters])

  const handleClear = useCallback(() => {
    const isDefault =
      filters.time === defaultFilters.time &&
      filters.status === defaultFilters.status &&
      filters.eid === defaultFilters.eid &&
      filters.start === defaultFilters.start &&
      filters.end === defaultFilters.end

    if (isDefault) return

    resetFilters()
    if (onChange) {
      onChange(defaultFilters)
    }
  }, [filters, resetFilters, onChange])

  const handleCloseModal = useCallback(() => setDateModalOpen(false), [])

  const isTimeActive = useCallback((range) => filters.time === range, [filters.time])

  const isCustomDateActive = useMemo(
    () => filters.time === 'custom' && filters.start && filters.end,
    [filters.time, filters.start, filters.end]
  )

  const displayDateLabel = useMemo(() => {
    if (isCustomDateActive) {
      return `${moment.unix(filters.start).format('DD/MM/YYYY')} - ${moment
        .unix(filters.end)
        .format('DD/MM/YYYY')}`
    }
    return 'Date'
  }, [isCustomDateActive, filters.start, filters.end])

  return (
    <div className='filter-bar-wrapper'>
      <div className='filter-bar-container'>
        <span className='filter-label'>FILTER:</span>

        <div className='filter-chips'>
          <button
            type='button'
            className={`filter-chip ${isTimeActive(DASHBOARD_TIME_RANGE.DAYS_90) ? 'active' : ''}`}
            onClick={() => handleTimeRangeChange(DASHBOARD_TIME_RANGE.DAYS_90)}
          >
            90 days
          </button>
          <button
            type='button'
            className={`filter-chip ${isTimeActive(DASHBOARD_TIME_RANGE.THIS_MONTH) ? 'active' : ''}`}
            onClick={() => handleTimeRangeChange(DASHBOARD_TIME_RANGE.THIS_MONTH)}
          >
            This Month
          </button>
          <button
            type='button'
            className={`filter-chip ${isTimeActive(DASHBOARD_TIME_RANGE.THIS_WEEK) ? 'active' : ''}`}
            onClick={() => handleTimeRangeChange(DASHBOARD_TIME_RANGE.THIS_WEEK)}
          >
            This Week
          </button>
          <button
            type='button'
            className={`filter-chip ${isCustomDateActive ? 'active' : ''}`}
            onClick={() => setDateModalOpen(true)}
          >
            <FaCalendarAlt style={{ marginRight: 6, verticalAlign: 'middle' }} />
            {displayDateLabel}
          </button>
        </div>

        <div className='filter-divider' />

        <div className='filter-dropdowns'>
          <select
            id='filter-event-select'
            name='eventId'
            className={`filter-select ${filters.eid ? 'active' : ''}`}
            value={filters.eid || ''}
            onChange={handleEventChange}
            style={{ width: '140px' }}
          >
            <option value='' disabled hidden>Events</option>
            {events.map((event) => {
              const displayTitle =
                event.title && event.title.length > 30
                  ? `${event.title.substring(0, 30)}...`
                  : event.title
              return (
                <option key={event.id} value={event.id} title={event.title}>
                  {displayTitle}
                </option>
              )
            })}
          </select>

          <select
            id='filter-status-select'
            name='status'
            className={`filter-select ${filters.status ? 'active' : ''}`}
            value={filters.status || ''}
            onChange={handleStatusChange}
          >
            <option value='' disabled hidden>All Statuses</option>
            {FILTER_STATUSES_DASHBOARD.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
        </div>

        <div className='filter-divider' />

        <button type='button' className='filter-clear-btn' onClick={handleClear}>
          <span className='clear-icon'>×</span> Clear
        </button>
      </div>

      <SelectDateSyncData
        isOpen={dateModalOpen}
        onClose={handleCloseModal}
        onChangeRangeDate={handleCustomDateChange}
      />
    </div>
  )
}

export default React.memo(FilterBar)
